<template>
  <div class="legal-shell">
    <header class="legal-top">
      <nav class="legal-nav">
        <router-link to="/" class="brand">
          <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy" />
          <span>OilBuddy AI</span>
        </router-link>
        <div class="doc-switch">
          <router-link to="/privacy">Privacy Policy</router-link>
          <router-link to="/terms">Terms of Service</router-link>
          <router-link to="/data-deletion" class="on">Data Deletion</router-link>
          <router-link to="/contact" class="contact">Contact</router-link>
        </div>
      </nav>
    </header>

    <main class="wrap">
      <article class="document">
        <p class="kicker">OilBuddy AI — Privacy request</p>
        <h1>Request deletion of your data</h1>
        <p class="lede">
          Use this page to ask OilBuddy to delete your personal account data. This is the official
          data-deletion request link for oilbuddy.ai and the OilBuddy field applications.
          Document reference: <b>OB-LEG-DEL-001</b>.
        </p>

        <section>
          <h2>What we delete</h2>
          <ul>
            <li>Your login, profile name, email, phone, and authentication tokens.</li>
            <li>Job-marketplace profile details you entered as a contractor.</li>
            <li>Personal settings, notification preferences, and session data.</li>
          </ul>
        </section>

        <section>
          <h2>What we may retain</h2>
          <p>
            Oilfield commercial and safety records are often shared with a service company, company man, or operator.
            We may keep de-identified or company-owned copies of:
          </p>
          <ul>
            <li>Field tickets, invoices, and payment records required for tax or customer audit (typically up to seven years).</li>
            <li>Timesheets and payroll entries the employer must keep under wage and tax rules.</li>
            <li>Tank, well-head, daily report, or HSE records that belong to a company workspace you worked in.</li>
            <li>Information we must keep under a legal hold or valid legal process.</li>
          </ul>
          <p>
            Full detail is in Article 10 of the
            <router-link to="/privacy">Privacy Policy</router-link>.
          </p>
        </section>

        <section>
          <h2>How to submit</h2>
          <ol>
            <li>Complete the form below with the email on the OilBuddy account.</li>
            <li>Type <b>DELETE</b> to confirm.</li>
            <li>If you are signed in with that same email, we close the account immediately.</li>
            <li>If you are not signed in, we verify the request and complete it, usually within 30 days.</li>
          </ol>
          <p>
            You may also email
            <a href="mailto:contact@oilbuddy.ai?subject=Data%20deletion%20request%20OB-LEG-DEL-001">contact@oilbuddy.ai</a>
            with the subject “Data deletion request.”
          </p>
        </section>

        <form v-if="!done" class="form" @submit.prevent="submit">
          <label>Full name
            <input v-model="form.fullName" type="text" required autocomplete="name" />
          </label>
          <label>Account email
            <input v-model="form.email" type="email" required autocomplete="email" />
          </label>
          <label>Account type
            <select v-model="form.accountType">
              <option value="">Select…</option>
              <option value="contractor">Independent contractor</option>
              <option value="company">Service company</option>
              <option value="employee">W-2 employee</option>
              <option value="driver">Driver</option>
              <option value="company_man">Company man</option>
              <option value="other">Other / I am not sure</option>
            </select>
          </label>
          <label>What should we delete?
            <select v-model="form.requestType">
              <option value="account">Close my account and delete personal data</option>
              <option value="profile">Delete marketplace / profile data only</option>
              <option value="export-then-delete">Send me a copy first, then delete</option>
            </select>
          </label>
          <label>Additional details (optional)
            <textarea v-model="form.details" rows="4" placeholder="Well names, ticket numbers, or a company workspace we should notify."></textarea>
          </label>
          <label>Type DELETE to confirm
            <input v-model="form.confirmation" type="text" required placeholder="DELETE" autocomplete="off" />
          </label>
          <p v-if="error" class="err">{{ error }}</p>
          <button type="submit" class="submit" :disabled="saving">
            {{ saving ? 'Submitting…' : 'Submit deletion request' }}
          </button>
        </form>

        <div v-else class="done">
          <h2>Request received</h2>
          <p>{{ message }}</p>
          <p v-if="reference" class="ref">Reference #{{ reference }}</p>
          <router-link to="/privacy">Return to Privacy Policy</router-link>
        </div>
      </article>
    </main>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { useUserStore } from '@/stores/userStore'

const userStore = useUserStore()
const saving = ref(false)
const done = ref(false)
const error = ref('')
const message = ref('')
const reference = ref<number | null>(null)

const form = reactive({
  fullName: '',
  email: '',
  accountType: '',
  requestType: 'account',
  details: '',
  confirmation: '',
})

onMounted(() => {
  const user = userStore.currentUser
  if (user) {
    form.fullName = user.name || ''
    form.email = user.email || ''
    form.accountType = user.userType || ''
  }
})

async function submit() {
  error.value = ''
  saving.value = true
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const token = localStorage.getItem('auth_token')
    if (token) headers.Authorization = `Bearer ${token}`
    const res = await fetch('/api/privacy/delete-request.php', {
      method: 'POST',
      headers,
      body: JSON.stringify(form),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Could not submit request')
    if (data.demoBlocked) throw new Error(data.message)
    done.value = true
    message.value = data.message
    reference.value = data.id || null
    if (data.accountClosed) userStore.logout()
  } catch (e: any) {
    error.value = e.message || 'Could not submit request'
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.legal-shell { min-height: 100vh; background: #0b1220; color: #e2e8f0; }
.legal-top { border-bottom: 1px solid #1e293b; }
.legal-nav {
  max-width: 880px; margin: 0 auto; padding: 14px 24px;
  display: flex; justify-content: space-between; align-items: center; gap: 16px; flex-wrap: wrap;
}
.brand { display: flex; align-items: center; gap: 10px; color: #fff; font-weight: 700; }
.brand img { width: 36px; height: 36px; border-radius: 10px; }
.doc-switch { display: flex; gap: 16px; flex-wrap: wrap; font-size: 13px; }
.doc-switch a { color: #94a3b8; }
.doc-switch a.on, .doc-switch a:hover { color: #34d399; }
.doc-switch .contact { color: #67e8f9; }
.wrap { max-width: 880px; margin: 0 auto; padding: 36px 24px 72px; }
.document {
  background: #f8fafc; color: #0f172a; border-radius: 4px; padding: 44px 48px;
  box-shadow: 0 24px 60px rgba(0,0,0,.35);
}
.kicker { font-size: 11px; text-transform: uppercase; letter-spacing: .12em; color: #047857; font-weight: 700; }
h1 { font-size: 32px; margin: 8px 0 14px; }
.lede, section p, li { color: #1e293b; line-height: 1.65; }
section { margin: 28px 0; }
h2 { font-size: 18px; margin-bottom: 10px; }
ul, ol { margin: 0 0 12px 1.2rem; }
li { margin-bottom: 6px; }
a { color: #047857; font-weight: 600; }
.form { display: grid; gap: 14px; margin-top: 8px; }
label { font-size: 13px; font-weight: 600; color: #334155; display: grid; gap: 6px; }
input, select, textarea {
  width: 100%; background: #fff; border: 1px solid #cbd5e1; border-radius: 8px;
  padding: 10px 12px; color: #0f172a; font: inherit;
}
.err { color: #b91c1c; font-size: 14px; }
.submit {
  background: #b91c1c; color: #fff; border: 0; border-radius: 8px;
  padding: 12px 16px; font-weight: 700; cursor: pointer;
}
.submit:disabled { opacity: .6; cursor: not-allowed; }
.done { background: #ecfdf5; border-left: 4px solid #059669; padding: 16px; }
.ref { font-family: ui-monospace, monospace; color: #047857; }

@media (max-width: 700px) {
  .document { padding: 28px 18px; }
}
</style>
